import { Card, CardContent } from "@patient-watch/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@patient-watch/ui/form";
import { Label } from "@patient-watch/ui/label";
import { RadioGroup, RadioGroupItem } from "@patient-watch/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@patient-watch/ui/select";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

interface Props {
    name: string;
    label: string;
    required?: boolean;
}

interface TreatmentState {
    hasTried: boolean | null;
    duration: string;
    isOngoing: "yes" | "no" | null;
}

const durationOptions = [
    { label: "Less than 1 month", value: "less_than_1_month" },
    { label: "1 to 3 months", value: "1_to_3_months" },
    { label: "3 to 6 months", value: "3_to_6_months" },
    { label: "6 to 12 months", value: "6_to_12_months" },
    { label: "More than 12 months", value: "more_than_12_months" },
];

const TriedTreatment = ({ name, label, required = false }: Props) => {
    const { control, setValue, getValues } = useFormContext();

    const [state, setState] = useState<TreatmentState>(() => {
        // Initialize from form values on mount
        const formValue = getValues(name) as TreatmentState | undefined;
        if (formValue && formValue.hasTried !== undefined) {
            return {
                hasTried: formValue.hasTried ?? null,
                duration: formValue.duration ?? "",
                isOngoing: formValue.isOngoing ?? null,
            };
        }
        return {
            hasTried: null,
            duration: "",
            isOngoing: null,
        };
    });

    // Initialize form value on mount if it doesn't exist
    useEffect(() => {
        const formValue = getValues(name);
        if (!formValue || formValue.hasTried === undefined) {
            setValue(name, {
                hasTried: null,
                duration: "",
                isOngoing: null,
            });
        }
    }, [name, setValue, getValues]);

    const updateState = (updates: Partial<TreatmentState>) => {
        const newState = { ...state, ...updates };
        setState(newState);

        // Update form values - set the entire object at once
        setValue(name, {
            hasTried: newState.hasTried,
            duration: newState.duration,
            isOngoing: newState.isOngoing,
        });
    };

    return (
        <Card>
            <CardContent className="p-4">
                <FormField
                    control={control}
                    name={name}
                    render={() => (
                        <FormItem className="space-y-6">
                            <FormLabel className="font-normal text-base">
                                Have you tried {label}?{required && <span className="ml-0.5 text-red-500">*</span>}
                            </FormLabel>
                            <FormControl>
                                <RadioGroup
                                    value={state.hasTried === null ? "" : state.hasTried ? "yes" : "no"}
                                    onValueChange={(value) => {
                                        updateState({
                                            hasTried: value === "yes",
                                        });
                                    }}
                                    className="flex flex-row space-x-4"
                                >
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="yes" id={`${name}-tried-yes`} />
                                        <Label htmlFor={`${name}-tried-yes`}>Yes</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="no" id={`${name}-tried-no`} />
                                        <Label htmlFor={`${name}-tried-no`}>No</Label>
                                    </div>
                                </RadioGroup>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                {state.hasTried === true && (
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label className="block mb-2">I tried it for</Label>
                            <Select value={state.duration} onValueChange={(value) => updateState({ duration: value })}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select duration" />
                                </SelectTrigger>
                                <SelectContent>
                                    {durationOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="block mb-2">Are you still trying {label}?</Label>
                            <RadioGroup
                                value={state.isOngoing ?? ""}
                                onValueChange={(value: "yes" | "no") =>
                                    updateState({
                                        isOngoing: value,
                                    })
                                }
                                className="flex flex-row space-x-4"
                            >
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="yes" id={`${name}-yes`} />
                                    <Label htmlFor={`${name}-yes`}>Yes</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="no" id={`${name}-no`} />
                                    <Label htmlFor={`${name}-no`}>No</Label>
                                </div>
                            </RadioGroup>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default TriedTreatment;
